import { useEffect, useState } from 'react';
import { Star, TrendingUp, TrendingDown, Trash2, Eye } from 'lucide-react';
import { Stock, Watchlist } from '../lib/supabase';
import { marketService } from '../lib/marketService';
import { useAuth } from '../contexts/AuthContext';

interface WatchlistViewProps {
  onStockClick: (stock: Stock) => void;
}

export const WatchlistView = ({ onStockClick }: WatchlistViewProps) => {
  const [watchlist, setWatchlist] = useState<Watchlist[]>([]);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) loadWatchlist();
    else setLoading(false);
  }, [user]);

  const loadWatchlist = async () => {
    if (!user) return;
    try {
      const watchlistData = await marketService.getWatchlist(user.id);
      setWatchlist(watchlistData);
      const allStocks = await marketService.getStocks();
      const watchedStocks = allStocks.filter(stock => watchlistData.some(w => w.stock_code === stock.code));
      setStocks(watchedStocks);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (stockCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    try {
      await marketService.removeFromWatchlist(user.id, stockCode);
      setWatchlist(watchlist.filter(w => w.stock_code !== stockCode));
      setStocks(stocks.filter(s => s.code !== stockCode));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (!user) {
    return (
      <div className="card p-12 text-center">
        <Star className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Watchlist</h3>
        <p className="text-slate-400">Silakan login untuk menggunakan fitur watchlist</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (stocks.length === 0) {
    return (
      <div className="card p-12 text-center">
        <Star className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Watchlist Kosong</h3>
        <p className="text-slate-400">Tambahkan saham ke watchlist dari halaman Screener</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {stocks.map((stock) => {
        const change = marketService.calculateChange(stock.price, stock.prev_close);
        const changePercent = marketService.calculateChangePercent(stock.price, stock.prev_close);
        const isPositive = change >= 0;

        return (
          <div
            key={stock.id}
            onClick={() => onStockClick(stock)}
            className="card-hover p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center">
                  <span className="font-bold text-emerald-400">{stock.code.slice(0, 2)}</span>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-white">{stock.code}</span>
                    <span className="badge-blue text-xs">{stock.sector}</span>
                  </div>
                  <p className="text-sm text-slate-500">{stock.name}</p>
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <div className="text-right">
                  <div className="font-mono font-bold text-white">{marketService.formatPrice(stock.price)}</div>
                  <div className={`flex items-center justify-end space-x-1 text-sm ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span className="font-mono">{isPositive ? '+' : ''}{changePercent.toFixed(2)}%</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); onStockClick(stock); }}
                    className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4 text-slate-400" />
                  </button>
                  <button
                    onClick={(e) => removeFromWatchlist(stock.code, e)}
                    className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
